/**
 * @render react
 * @name ProductSelector
 */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withPolaris } from 'Components/protons/Polaris';
import { Button } from 'Components/atoms/Button';
import { Select } from 'Components/atoms/Select';
import { List } from 'Components/molecules/List';

import './_style.scss';

/**
 * ProductSelector component
 *
 * @param {Object} context - Extra information coming from Polaris provider
 * @param {string} ctaLabel - Call to action label
 * @param {Object} extraClassNames - Extra class names to be applied to the component
 * @param {boolean} groupOptions - Group the options
 * @param {array} inputs - Array of objects with the makes and models
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {string} mainTitle - Main title
 * @param {array} popularInputs - Array of strings with the most popular makes
 * @param {string} popularInputsLabel - Label for the most popular makes
 * @param {boolean} showCta - Show the call-to-action button
 * @param {string} title - Title
 */
class ProductSelector extends PureComponent {
  /**
   * Constructor
   * @param props
   */
  constructor(props) {
    super(props);

    this.state = {
      loaded: false,
      make: null,
      makeLabel: null,
      model: null,
      url: null,
      groupHeading: 'All makes (A - Z)',
    };

    this.onChange = this.onChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }

  /**
   * componentDidMount
   * used to identify if ssr by setting state on hydration
   */

  componentDidMount() {
    this.setState({
      loaded: true,
    });
  }

  /**
   * Handles input change events for the product selector.
   *
   * This method updates the text content of group headings.
   * If the query is not empty and the heading text is 'All makes (A - Z)', it changes the heading text to 'Results'.
   * Additionally, if the action is 'input-change' and the query is empty, it resets the heading text to 'All makes (A - Z)'.
   *
   * @param {string} query - The input query to filter the group headings.
   * @param {Object} param1 - An object containing the action type.
   * @param {string} param1.action - The action type, e.g., 'input-change'.
   */
  handleInputChange = (query, { action }) => {
    if (query === null || query === undefined) return;
    const { groupHeading } = this.state;
    const shouldShowResults = query && groupHeading === 'All makes (A - Z)';
    const shouldResetHeading = action === 'input-change' && !query && groupHeading === 'Results';

    if (shouldShowResults || shouldResetHeading) {
      this.setState({
        groupHeading: query ? 'Results' : 'All makes (A - Z)',
      });
    }
  };

  /**
   * Handler for the onChange event for the selects.
   * @param {SyntheticEvent} e - Event when a select or input is changed
   * @param option
   */
  onChange(e, option) {
    const { label, value } = e; // in case on make: label=make name, value=make ID, in case of model label=model name, value=model URL
    const { name } = option;
    const {
      showCta,
    } = this.props;

    // If we change the make, filter the model
    if (name === 'make') {
      this.setState({
        make: value, // make ID
        makeLabel: label, // make Name
        model: null,
        url: null,
      });
    } else if (name === 'model') {
      if (showCta) {
        // Update the state with the value URL if showCta is true
        this.setState({
          model: e, // model Name and URL
          url: value, // model URL
        });
      } else {
        const { makeLabel } = this.state;
        if (typeof window !== 'undefined') {
          window.dataLayer.push({
            event: 'findreview.button_click',
            dropdown_make: makeLabel, // make Name
            dropdown_model: label, // model Name
          });
        }

        // Redirect if we change a model & showCta is false
        window.location.href = value; // model URL
      }
    }
  }

  /**
   * Handler for the onSubmit event for the selects
   */
  onSubmit() {
    const { url } = this.state;

    if (url) {
      window.location.href = url;
    }
  }

  /**
   * Render
   */
  render() {
    const {
      context: { cssPrefix, makeModelData: makeModelDataContext },
      ctaLabel,
      extraClassNames,
      groupOptions,
      inputs,
      loadStyles,
      mainTitle,
      popularInputs,
      popularInputsLabel,
      showCta,
      title,
    } = this.props;
    const {
      make, loaded, model, groupHeading,
    } = this.state;

    const makeModelData = inputs || makeModelDataContext;

    const makeOptions = makeModelData.map(({ name, id }) => ({ label: name, value: id }));

    // Get the most popular makes, and return them in the order they are in the popularInputs array
    const mostPopular = makeOptions
      .filter(({ label }) => popularInputs.includes(label))
      .sort((a, b) => popularInputs.indexOf(a.label) - popularInputs.indexOf(b.label));

    const modelOptions = (
      (makeModelData.find(({ id }) => make === id) || {}).data || []
    ).map(({ name, url }) => ({ label: name, value: url }));

    const groupedOptions = [
      {}, // empty object to force the focus on the first option in the dropdown
      {
        label: popularInputsLabel,
        options: mostPopular,
        className: `${cssPrefix}__group--popular-makes`,
      },
      {
        label: groupHeading,
        options: makeOptions,
        className: `${cssPrefix}__group--all-makes`,
      },
    ];

    return (
      <nav
        className={classNames({
          [`${cssPrefix}__product-selector`]: loadStyles,
          ...extraClassNames,
        })}
      >
        <div
          className={classNames({
            [`${cssPrefix}__product-selector--inner`]: true,
          })}
        >

          {(mainTitle || title) && (
            <div className={`${cssPrefix}__product-selector--intro`}>
              {mainTitle && (
                <div className={`${cssPrefix}__product-selector--main-title`}>
                  {mainTitle}
                </div>
              )}
              {title && (
                <div className={`${cssPrefix}__product-selector--title`}>
                  {title}
                </div>
              )}
            </div>
          )}

          <div className={`${cssPrefix}__product-selector--inputs`}>
            <div className={`${cssPrefix}__product-selector--input`}>
              <Select
                id={`${cssPrefix}__product-selector--make`}
                name="make"
                options={groupOptions ? groupedOptions : makeOptions}
                onChange={loaded ? this.onChange : null}
                placeholder="Make"
                extraClassNames={{
                  [`${cssPrefix}__product-selector--select`]: true,
                }}
                reactSelectProps={{
                  'aria-label': 'Make',
                  onInputChange: loaded && groupOptions ? this.handleInputChange : null,
                }}
              />

              { // css dropdown for when js is disabled (for seo purposes)
                !loaded && (
                  <div className={`${cssPrefix}__select ${cssPrefix}__select-nojs`}>
                    <input type="checkbox" id="menu-trigger" />
                    {/* eslint-disable-next-line */}
                    <label htmlFor="menu-trigger" />
                    <div className={`${cssPrefix}__menu`}>
                      <div className={`${cssPrefix}__menu-list`}>
                        <List
                          content={
                            makeModelData.map(item => ({
                              url: item.url || `/${item.name.replace(' ', '-').toLowerCase()}`,
                              title: item.name,
                              extraClassNames: {
                                [`${cssPrefix}__option`]: true,
                              },
                            }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                )
              }

            </div>
            <div className={`${cssPrefix}__product-selector--input`}>
              <Select
                id={`${cssPrefix}__product-selector--model`}
                name="model"
                options={modelOptions}
                onChange={this.onChange}
                isDisabled={!make}
                placeholder="Model"
                extraClassNames={{
                  [`${cssPrefix}__product-selector--select`]: true,
                }}
                reactSelectProps={{
                  'aria-label': 'Model',
                  value: model || '',
                }}
              />
            </div>
            {showCta && (
              <div className={`${cssPrefix}__product-selector--button-container`}>
                <Button
                  type="button"
                  label={ctaLabel}
                  onClick={this.onSubmit}
                  extraClassNames={{
                    [`${cssPrefix}__product-selector--button`]: true,
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </nav>
    );
  }
}

/**
 * Valid props
 */
ProductSelector.propTypes = {
  context: PropTypes.shape({
    makeModelData: PropTypes.arrayOf(PropTypes.shape()),
    cssPrefix: PropTypes.string,
  }),
  ctaLabel: PropTypes.string,
  extraClassNames: PropTypes.objectOf(PropTypes.bool),
  groupOptions: PropTypes.bool,
  inputs: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.string,
  })),
  loadStyles: PropTypes.bool,
  mainTitle: PropTypes.string,
  popularInputs: PropTypes.arrayOf(PropTypes.string),
  popularInputsLabel: PropTypes.string,
  showCta: PropTypes.bool,
  title: PropTypes.string,
};

/**
 * Default props
 */
ProductSelector.defaultProps = {
  context: {
    makeModelData: [],
  },
  ctaLabel: '',
  extraClassNames: {},
  groupOptions: false,
  inputs: null,
  loadStyles: true,
  mainTitle: '',
  popularInputs: [],
  popularInputsLabel: 'Most popular searches',
  showCta: false,
  title: 'Find a car review',
};

export default withPolaris(ProductSelector);
